import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FC, PropsWithChildren, ReactNode } from "react";
import { Link } from "react-router-dom";
import Text from "./Text";

type Variant = "primary" | "danger" | "secondary" | "plain" | "outlined";

interface ButtonBaseProps extends Omit<MuiButtonProps, "variant"> {
  variant?: Variant;
  isLoading?: boolean;
  withPaper?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  to?: string;
  paperText?: string;
}

const ButtonContainer = styled("div")<{}>(({ theme }) => ({
  borderRadius: "12px",
  padding: "16px",
  width: "100%",

  "&.MuiButton-withPaper": {
    background: theme.myTheme.colors.tertiary,
  },
}));

const StyledButton = styled(MuiButton)(({ theme }) => ({
  borderRadius: "12px",
  padding: "8px 16px",
  width: "100%",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  position: "relative",
  color: theme.myTheme.colors.foreground100,

  "&.MuiButton-primary": {
    background: theme.myTheme.colors.primary100,

    "&:hover": {
      opacity: 0.8,
    },

    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },

  "&.MuiButton-danger": {
    background: theme.myTheme.colors.destructive,

    "&:hover": {
      opacity: 0.8,
    },
  },

  "&.MuiButton-secondary": {
    background: theme.myTheme.colors.secondary,

    "&:hover": {
      opacity: 0.8,
    },
  },

  "&.MuiButton-plain": {
    background: "transparent",

    "&:hover": {
      opacity: 0.8,
    },
  },

  "&.MuiButton-outlined": {
    background: "transparent",
    border: `1px solid ${theme.myTheme.colors.foreground50} !important`,

    "&:hover": {
      opacity: 0.8,
    },
  },

  "&.Mui-disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  "&.loading": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  "& .button-content": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    gap: "8px",
  },

  "&.loading .button-content": {
    display: "none",
  },

  "& .start-icon, & .end-icon": {
    display: "flex",
    marginLeft: "auto",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MyButton: FC<PropsWithChildren<ButtonBaseProps>> = ({
  children,
  variant = "primary",
  className,
  isLoading = false,
  withPaper = false,
  disabled,
  startIcon,
  endIcon,
  to,
  paperText,
  ...props
}) => {
  const isDisabled = disabled || isLoading;

  const renderButtonContent = () => (
    <span className="button-content">
      {startIcon && <span className="start-icon">{startIcon}</span>}
      {children}
      {endIcon && <span className="end-icon">{endIcon}</span>}
    </span>
  );

  const component = to ? Link : undefined;

  const buttonProps = {
    ...props,
    className: `MuiButton-${variant} ${isLoading ? "loading" : ""} ${className || ""}`,
    disableElevation: true,
    disabled: isDisabled,
    component,
    to,
  };

  if (withPaper || paperText) {
    return (
      <ButtonContainer className={`MuiButton-withPaper ${className}`}>
        {paperText && (
          <Text variant="s" mb="16px">
            {paperText}
          </Text>
        )}
        <StyledButton {...buttonProps}>
          {renderButtonContent()}
          {isLoading && <CircularProgress size={24} color="inherit" />}
        </StyledButton>
      </ButtonContainer>
    );
  }

  return (
    <StyledButton {...buttonProps}>
      {renderButtonContent()}

      {isLoading && <CircularProgress size={24} color="inherit" />}
    </StyledButton>
  );
};

export default MyButton;
